<template>
    <BarChart ref="barChart" :chart-data="chartData" :options="options"/>
</template>

<script setup lang="ts">
import {computed, onUnmounted, ref} from 'vue';
import {BarChart} from 'vue-chart-3';
import {BarOptions, Chart, registerables} from "chart.js";
import ChartDataLabels, {Context} from 'chartjs-plugin-datalabels';

 const toPersianDigits = (s: string) => {
    return s.replace(/\d/g, (d) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return "۰۱۲۳۴۵۶۷۸۹"[d];
    });
};

const ThemeName = localStorage.getItem('theme')

const props = defineProps<{
    labels: string[],
    prices: number[],
    amounts: number[]
}>()

onUnmounted(() => {
    Chart.unregister(ChartDataLabels, ...registerables)
})

const lightenPercentage = 20;
const minPercentageTrunc = 30
const minPercentageDotsTrunc = 10

const barChart = ref()

Chart.register(ChartDataLabels, ...registerables);

function shadeColor(color: string, percent: number) {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);

    R = parseInt((R * (100 + percent) / 100).toString());
    G = parseInt((G * (100 + percent) / 100).toString());
    B = parseInt((B * (100 + percent) / 100).toString());

    R = (R < 255) ? R : 255;
    G = (G < 255) ? G : 255;
    B = (B < 255) ? B : 255;

    R = Math.round(R)
    G = Math.round(G)
    B = Math.round(B)

    const RR = ((R.toString(16).length == 1) ? "0" + R.toString(16) : R.toString(16));
    const GG = ((G.toString(16).length == 1) ? "0" + G.toString(16) : G.toString(16));
    const BB = ((B.toString(16).length == 1) ? "0" + B.toString(16) : B.toString(16));

    return "#" + RR + GG + BB;
}

const backgroundColors = computed(() => {
    return props.prices.map((item, key) => {
        return item > 0
            ? shadeColor(ThemeName === 'dark' ? "#388E3C" : '#43A047', (key + 1) * lightenPercentage)
            : shadeColor("#CC4956", (key + 1) * lightenPercentage);
    });
})

const chartData = computed(() => ({
    labels: props.labels,
    datasets: [
        {
            fill: true,
            backgroundColor: backgroundColors.value,
            data: [...props.prices],
        },
    ],
}));

const options = computed<BarOptions>(() => {
    return {
        pointRadius: 0,
        color: 'white',
        borderRadius: {
            topLeft: 100,
            topRight: 100,
            bottomLeft: 100,
            bottomRight: 100,
        },
        base: 1,
        borderSkipped: 'start',
        inflateAmount: 'auto',
        borderWidth: 0,
        borderColor: "#ffcb00",
        backgroundColor: '#ffcb00',
        barThickness: 30,
        pointStyle: 'dash',
        pointHitRadius: 10,
        indexAxis: 'y',
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                color: '#0F222A',
                font: {
                    family: 'IRANSansX',
                    size: 14,
                    weight: 'bold'
                },
                align: 'start',
                anchor: 'end',
                textAlign: 'center',
                formatter: function (value: number, context: Context) {
                    if (props.amounts === undefined || !Array.isArray(props.amounts) || props.amounts.length == 0)
                        return ''

                    const amount: null | undefined | number = props.amounts[context.dataIndex]
                    if (amount == null)
                        return ''

                    let label = toPersianDigits(amount.toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                        signDisplay: "never"
                    }))

                    let toAdd = 0
                    const minimumNegativeBar = Math.min(...props.prices)
                    if (minimumNegativeBar < 0) {
                        toAdd = minimumNegativeBar
                    }

                    const diff = (Math.abs(value) / Math.max(...props.prices) * 100);
                    if ((diff + toAdd) <= minPercentageTrunc) {
                        if (Math.abs(diff) > minPercentageDotsTrunc)
                            return '...'

                        return ''
                    }

                    if (amount < 0)
                        label += '-'

                    return label + ' ت'
                },
            },
            tooltip: {
                enabled: true,
                displayColors: false,
                bodyColor: "#0F222A",
                backgroundColor: '#FFFFFF',
                yAlign: 'bottom',
                xAlign: 'center',
                mode: 'nearest',
                bodyFont: {
                    weight: 'bold',
                    size: 14,
                    family: 'IRANSansX',
                },
                rtl: true,
                boxPadding: 8,
                filter: function (_data: unknown) {
                    return true
                    // return (Math.abs(props.Prices[tooltipItem.dataIndex]) / Math.max(...props.Prices)) * 100 <= minPercentageTrunc;
                },
                callbacks: {
                    title: function (_tooltipItems: unknown, _data: unknown) {
                        return null
                    },
                    label: function (tooltipItems: { dataIndex: number }, _data: unknown) {
                        // if (((Math.abs(props.Prices[tooltipItems.dataIndex]) / Math.max(...props.Prices)) * 100) > minPercentageTrunc) {
                        //     return null
                        // }
                        if (props.amounts === undefined || !Array.isArray(props.amounts) || props.amounts.length == 0)
                            return ''

                        const amount = Number(props.amounts[tooltipItems.dataIndex]);
                        let label = ""

                        label += amount.toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                            signDisplay: "never"
                        })

                        if (amount < 0) {
                            label += '-'
                        }
                        return label + ' ت'
                    },
                }
            },
        },
        scales: {
            xAxis: {
                grid: {
                    borderDash: [5, 5],
                    autoSkip: true,
                    display: true,
                    color: ThemeName === 'dark' ? '#676464' : '#83837a',
                    drawTicks: false,
                },
                ticks: {
                    color: ThemeName === 'dark' ? '#CBDAE1' : '#647F8B',
                    padding: 10,
                    font: {
                        size: 12,
                        family: 'IRANSansX',
                    },
                }
            },
            yAxis: {
                type: 'category',
                display: true,
                position: 'left',
                grid: {
                    autoSkip: true,
                    display: false,
                    color: ThemeName === 'dark' ? '#475F69' : '#E2ECF0',
                    borderColor: 'transparent',
                    drawTicks: false,
                },
                ticks: {
                    color: ThemeName === 'dark' ? '#CBDAE1' : '#647F8B',
                    padding: 10,
                    font: {
                        size: 12,
                        family: 'IRANSansX',
                    },
                },
            },
        },
    };
});
</script>